import { Icon, IconButton, Stack, Text, tokens } from '@/design';
import type { ThreeDCameraMode } from '@/state/useViewerStore';

interface ThreeDToolbarProps {
  activeMode: ThreeDCameraMode;
  onModeChange(mode: ThreeDCameraMode): void;
  onResetView(): void;
  onLookAtSelected(): void;
  disableLookAtSelected?: boolean;
}

interface CameraTool {
  mode: ThreeDCameraMode;
  icon:
    | '360'
    | 'pan_tool'
    | 'zoom_in'
    | 'directions_walk'
    | 'flight'
    | 'center_focus_strong';
  label: string;
  shortcut: string;
}

const CAMERA_TOOLS: ReadonlyArray<CameraTool> = [
  { mode: 'orbit', icon: '360', label: 'Orbit', shortcut: 'Drag' },
  { mode: 'pan', icon: 'pan_tool', label: 'Pan', shortcut: 'Shift+Drag' },
  { mode: 'zoom', icon: 'zoom_in', label: 'Zoom', shortcut: '+ / -' },
  { mode: 'walk', icon: 'directions_walk', label: 'Walk', shortcut: 'W' },
  { mode: 'fly', icon: 'flight', label: 'Fly', shortcut: 'F' },
  {
    mode: 'look-at-selected',
    icon: 'center_focus_strong',
    label: 'Look at selected',
    shortcut: 'L',
  },
] as const;

export function ThreeDToolbar({
  activeMode,
  onModeChange,
  onResetView,
  onLookAtSelected,
  disableLookAtSelected = false,
}: ThreeDToolbarProps) {
  const buttonTone = (active: boolean) => ({
    background: active ? tokens.color.surface[3] : 'transparent',
  });

  return (
    <div
      role="toolbar"
      aria-label="3D camera controls"
      style={{
        position: 'absolute',
        top: tokens.space[2],
        right: tokens.space[2],
        zIndex: tokens.zIndex.panel,
        background: tokens.color.surface[1],
        border: tokens.border.thin,
        borderRadius: tokens.radius.md,
        padding: tokens.space[1],
        maxWidth: tokens.width.popoverLg,
      }}
    >
      <Stack direction="row" align="center" gap={1} style={{ flexWrap: 'wrap' }}>
        {CAMERA_TOOLS.map((tool) => {
          const isLookAt = tool.mode === 'look-at-selected';
          const disabled = isLookAt ? disableLookAtSelected : false;
          return (
            <IconButton
              key={tool.mode}
              aria-label={`${tool.label} (${tool.shortcut})`}
              aria-pressed={activeMode === tool.mode}
              onClick={() => {
                if (isLookAt) {
                  onLookAtSelected();
                  return;
                }
                onModeChange(tool.mode);
              }}
              disabled={disabled}
              style={buttonTone(activeMode === tool.mode)}
            >
              <Icon name={tool.icon} size={20} decorative />
            </IconButton>
          );
        })}
        <div
          aria-hidden
          style={{
            width: tokens.border.width.thin,
            alignSelf: 'stretch',
            background: tokens.color.surface[3],
          }}
        />
        <IconButton aria-label="Reset camera (R)" onClick={onResetView}>
          <Icon name="replay" size={20} decorative />
        </IconButton>
      </Stack>
      <Text as="p" variant="caption" style={{ margin: 0, marginTop: tokens.space[1], color: tokens.color.ink.secondary }}>
        Camera mode: {activeMode}
      </Text>
    </div>
  );
}
